import { AIModule } from '@/data/ai/types';

export const DATA_PIPELINE_ORCHESTRATION_MODULE: AIModule = {
  id: 'data-pipeline-orchestration',
  title: 'Pipeline Orchestration',
  description: 'Master workflow orchestration with Apache Airflow and modern orchestration tools.',
  duration: '2.5 hours',
  difficulty: 'intermediate',
  order: 2,
  content: `
## What You'll Learn
- Configure and use Apache Airflow for workflow orchestration
- Design DAGs (Directed Acyclic Graphs) for complex workflows
- Implement task dependencies and scheduling
- Handle failures and retries gracefully

## Overview
Orchestration tools manage the execution order, scheduling, and monitoring of data pipeline tasks. Apache Airflow is the industry standard.

---

## Key Concepts

### 1. Airflow DAGs
DAGs define the workflow structure:

\`\`\`python
from airflow import DAG
from airflow.operators.python import PythonOperator
from datetime import datetime, timedelta

default_args = {
    'retries': 3,
    'retry_delay': timedelta(minutes=5)
}

with DAG(
    'ml_training_pipeline',
    default_args=default_args,
    schedule_interval='@daily',
    start_date=datetime(2024, 1, 1)
) as dag:
    
    extract = PythonOperator(
        task_id='extract_data',
        python_callable=extract_data
    )
    
    transform = PythonOperator(
        task_id='transform_data',
        python_callable=transform_data
    )
    
    extract >> transform  # Set dependency
\`\`\`

### 2. Task Dependencies
Chain tasks with proper dependencies:

\`\`\`python
# Linear dependency
task1 >> task2 >> task3

# Parallel tasks
[task1, task2] >> task3

# Complex branching
from airflow.operators.python import BranchPythonOperator

def choose_branch(**context):
    if context['params']['model_type'] == 'deep':
        return 'train_deep_model'
    return 'train_simple_model'
\`\`\`

---

## Best Practices
- **Atomic Tasks**: Each task should do one thing well
- **Idempotent Tasks**: Safe to re-run without side effects
- **XCom for Data**: Pass small data between tasks
- **Sensors for Waiting**: Use sensors to wait for external events

---

## Key Takeaways
✅ Airflow orchestrates complex ML workflows
✅ DAGs define task dependencies visually
✅ Built-in retry logic handles transient failures
✅ Scheduling enables automated pipeline runs

## Resources
- [Airflow Best Practices](https://airflow.apache.org/docs/apache-airflow/stable/best-practices.html)
- [Prefect Documentation](https://docs.prefect.io/)
`,
  learningObjectives: [
    'Configure Apache Airflow',
    'Design effective DAGs',
    'Implement task dependencies',
    'Handle pipeline failures'
  ],
  quiz: {
    questions: [
      {
        id: 'dpo-q1',
        question: 'What does DAG stand for in Airflow?',
        options: ['Data Analysis Graph', 'Directed Acyclic Graph', 'Dynamic Automation Grid', 'Database Access Gateway'],
        correctAnswer: 1,
        explanation: 'DAG stands for Directed Acyclic Graph, representing a workflow with directed edges and no cycles.'
      },
      {
        id: 'dpo-q2',
        question: 'What operator is used for Python functions in Airflow?',
        options: ['BashOperator', 'PythonOperator', 'SQLOperator', 'DockerOperator'],
        correctAnswer: 1,
        explanation: 'PythonOperator is used to execute Python functions as tasks in Airflow.'
      },
      {
        id: 'dpo-q3',
        question: 'How do you set task1 to run before task2?',
        options: ['task1.before(task2)', 'task1 >> task2', 'task1 -> task2', 'task1.depends(task2)'],
        correctAnswer: 1,
        explanation: 'The >> operator in Airflow sets upstream/downstream dependencies between tasks.'
      },
      {
        id: 'dpo-q4',
        question: 'What is XCom used for?',
        options: ['External communication', 'Passing data between tasks', 'Logging', 'Scheduling'],
        correctAnswer: 1,
        explanation: 'XCom (cross-communication) allows tasks to exchange small amounts of data.'
      },
      {
        id: 'dpo-q5',
        question: 'Which schedule runs a DAG every day at midnight?',
        options: ['@hourly', '@weekly', '@daily', '@monthly'],
        correctAnswer: 2,
        explanation: '@daily schedule interval runs the DAG once every day at midnight UTC.'
      }
    ]
  },
  practice: {
    task: 'Create an ML Training DAG: Build an Airflow DAG that orchestrates data extraction, preprocessing, and model training. Create a DAG with extract, preprocess, train, and evaluate tasks with proper dependencies.',
    starter: `from airflow import DAG
from airflow.operators.python import PythonOperator
from datetime import datetime, timedelta

# TODO: Define default_args with retries

# TODO: Create DAG with daily schedule

# TODO: Define tasks for:
# 1. extract_data
# 2. preprocess_data  
# 3. train_model
# 4. evaluate_model

# TODO: Set dependencies (linear flow)`,
    solution: `from airflow import DAG
from airflow.operators.python import PythonOperator
from datetime import datetime, timedelta

default_args = {
    'owner': 'ml_team',
    'retries': 3,
    'retry_delay': timedelta(minutes=5),
    'email_on_failure': True
}

def extract_data(**context):
    print("Extracting data from source...")
    return {'rows': 1000}

def preprocess_data(**context):
    print("Preprocessing data...")
    return {'cleaned_rows': 950}

def train_model(**context):
    print("Training model...")
    return {'accuracy': 0.95}

def evaluate_model(**context):
    print("Evaluating model...")
    return {'f1_score': 0.93}

with DAG(
    'ml_training_pipeline',
    default_args=default_args,
    schedule_interval='@daily',
    start_date=datetime(2024, 1, 1),
    catchup=False
) as dag:
    
    extract = PythonOperator(
        task_id='extract_data',
        python_callable=extract_data
    )
    
    preprocess = PythonOperator(
        task_id='preprocess_data',
        python_callable=preprocess_data
    )
    
    train = PythonOperator(
        task_id='train_model',
        python_callable=train_model
    )
    
    evaluate = PythonOperator(
        task_id='evaluate_model',
        python_callable=evaluate_model
    )
    
    extract >> preprocess >> train >> evaluate`,
    hints: [
      'Use default_args for common settings',
      'Set catchup=False to avoid backfilling',
      'Use >> operator for dependencies'
    ],
    estimatedTime: 30
  }
};
